import { useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "flowbite-react";
import { HiCheck, HiOutlineExclamationCircle } from "react-icons/hi";
import { FaSync } from "react-icons/fa";

import { useHubspot } from "../../../../contexts";
import { HsPipelinesStep } from "./HsPipelinesStep";
import { HsFieldsMappingTable } from "./HsFieldsMappingTable";
import { HsReviewStep } from "./HsReviewStep";
import { getMappingProgress } from "./mappingUtils";
import { getRoleStatus, useVisibleRoles } from "./pipelineRoles";

const STEPS = [
  { key: "pipelines", label: "Pipelines" },
  { key: "mapping", label: "Field mapping" },
  { key: "review", label: "Review & enable" },
];

// The one-time HubSpot setup, shown until sync is enabled. Makes the implicit
// sequence explicit: configure pipelines -> map fields -> review & enable.
// Steps are freely navigable; only "Save and enable sync" is gated (on all
// fields being mapped), and the gate reason is always visible.
export const HsSetupStepper = () => {
  const {
    hubspotCustomizations,
    panoraFields,
    localMapping,
    handleSubmit,
    saveLoading,
    handleEnableSync,
    enableSyncLoading,
  } = useHubspot();
  const visibleRoles = useVisibleRoles();

  const [stepIndex, setStepIndex] = useState(0);
  const [showEnableConfirm, setShowEnableConfirm] = useState(false);

  const { mapped, total } = getMappingProgress(panoraFields, localMapping);
  const allMapped = total > 0 && mapped === total;

  const pipelinesComplete =
    !!hubspotCustomizations?.deal_revenue_type &&
    visibleRoles.every(
      (role) => getRoleStatus(role, hubspotCustomizations).configured,
    );
  const stepComplete = [pipelinesComplete, allMapped, false];

  const isLastStep = stepIndex === STEPS.length - 1;

  const saveMappings = () => {
    // Errors already surface as toasts inside handleSubmit.
    handleSubmit({}).catch(() => {});
  };

  const confirmEnableSync = () => {
    setShowEnableConfirm(false);
    handleEnableSync();
  };

  return (
    // One shell card: nav as header, step content recessed on gray, actions as
    // footer — so the steps visibly live inside the wizard instead of reading
    // as sibling cards.
    <section className="animation-slide-up-lil-bounce flex flex-col rounded-lg bg-white shadow">
      <nav className="flex items-center justify-center gap-2 border-b border-gray-200 px-4 py-3">
        {STEPS.map((step, index) => (
          <div key={step.key} className="flex items-center gap-2">
            {index > 0 && <div className="h-px w-8 bg-gray-200 sm:w-16" />}
            <button
              type="button"
              onClick={() => setStepIndex(index)}
              className={`flex items-center gap-2 rounded-full px-1.5 py-1.5 text-sm transition-colors ${
                index === stepIndex
                  ? "bg-blue-100"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                  stepComplete[index]
                    ? "bg-green-500 text-white"
                    : index === stepIndex
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                }`}
              >
                {stepComplete[index] ? (
                  <HiCheck className="h-3 w-3" />
                ) : (
                  index + 1
                )}
              </span>
              {step.label}
            </button>
          </div>
        ))}
      </nav>

      <div className="flex flex-col bg-gray-50 p-4 sm:p-6">
        {stepIndex === 0 && <HsPipelinesStep />}
        {stepIndex === 1 && <HsFieldsMappingTable />}
        {stepIndex === 2 && <HsReviewStep />}
      </div>

      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 rounded-b-lg border-t border-gray-200 bg-white px-4 py-3">
        <div>
          {stepIndex > 0 && (
            <Button
              color="gray"
              onClick={() => setStepIndex(stepIndex - 1)}
              disabled={saveLoading || enableSyncLoading}
            >
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isLastStep && !allMapped && (
            <p className="text-xs text-yellow-600">
              {mapped} of {total} fields mapped — finish the Field mapping step
              to enable sync.
            </p>
          )}
          <Button
            color="gray"
            onClick={saveMappings}
            disabled={saveLoading || enableSyncLoading}
            isProcessing={saveLoading}
          >
            Save progress
          </Button>
          {!isLastStep ? (
            <Button color="blue" onClick={() => setStepIndex(stepIndex + 1)}>
              Continue
            </Button>
          ) : (
            <Button
              color="blue"
              onClick={() => setShowEnableConfirm(true)}
              disabled={saveLoading || enableSyncLoading || !allMapped}
              isProcessing={enableSyncLoading}
            >
              <FaSync className="mr-2 mt-0.5 h-4 w-4" />
              Save and enable sync
            </Button>
          )}
        </div>
      </div>

      <Modal
        show={showEnableConfirm}
        size="md"
        onClose={() => setShowEnableConfirm(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-orange-400 dark:text-gray-200" />
            <p className="mb-8 text-lg font-normal">
              Are you sure you want to enable sync? Make sure you have mapped
              everything correctly. This is a one time action.
            </p>
            <div className="flex justify-center gap-4">
              <Button color="red" onClick={confirmEnableSync}>
                Yes, I'm sure
              </Button>
              <Button
                color="alternative"
                onClick={() => setShowEnableConfirm(false)}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </section>
  );
};
